import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Alert, PageHeader, StatusPill, datum, pct, useLoad } from '../shared/ui.js';

interface Entry {
  accountId: string;
  accountNumber: string;
  name: string;
  accountType: string;
  isActive: boolean;
  currentBaseDiscountPercent: number | null;
  currentValidFrom: string | null;
  activeDeviationCount: number;
  userCount: number;
  salesOwner: { userId: string; displayName: string } | null;
}

export const KONTOTYP: Record<string, string> = {
  distributor: 'Haendler',
  field_sales: 'Aussendienst',
  end_customer: 'Endkunde',
};

/** Kundenliste: zentraler Einstieg fuer Betreiber und Vertrieb. */
export function AccountsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [suche, setSuche] = useState('');
  const [aktiv, setAktiv] = useState<'alle' | 'true' | 'false'>('true');
  const [sort, setSort] = useState<'name' | 'accountNumber'>('name');
  const [owner, setOwner] = useState('');

  const params = new URLSearchParams({ sort, limit: '200' });
  if (suche !== '') params.set('q', suche);
  if (aktiv !== 'alle') params.set('active', aktiv);
  if (owner !== '') params.set('salesOwnerId', owner);

  const { data, error } = useLoad<{ items: Entry[]; total: number }>(
    `/api/admin/accounts?${params}`,
  );
  const intern = useLoad<{ items: Array<{ id: string; displayName: string }> }>(
    '/api/admin/accounts/internal-users',
  );

  return (
    <>
      <PageHeader
        title="Kunden"
        lead="Alle Vertriebspartner mit ihrer aktuell gueltigen Kondition."
        actions={
          <Link to="/admin/kunden/neu" className="button primary">
            Kunde anlegen
          </Link>
        }
      />
      <form
        className="filterbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSuche(q);
        }}
      >
        <input
          type="search"
          placeholder="Kundenname oder Kundennummer"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit">Suchen</button>
        <select
          value={aktiv}
          onChange={(e) => setAktiv(e.target.value as typeof aktiv)}
          aria-label="Status"
        >
          <option value="true">nur aktive</option>
          <option value="false">nur inaktive</option>
          <option value="alle">alle</option>
        </select>
        <select
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          aria-label="Zustaendiger Vertrieb"
        >
          <option value="">alle Vertriebler</option>
          {(intern.data?.items ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
          aria-label="Sortierung"
        >
          <option value="name">nach Name</option>
          <option value="accountNumber">nach Kundennummer</option>
        </select>
      </form>
      <Alert text={error} />
      {data !== null && (
        <div className="card">
          <p className="note">{data.total} Kunden</p>
          <div className="table-scroll">
            <table className="clickable">
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>Kundennummer</th>
                  <th>Typ</th>
                  <th>Status</th>
                  <th className="num">Grundrabatt</th>
                  <th className="num">Abweichungen</th>
                  <th>Gueltig ab</th>
                  <th className="num">Zugaenge</th>
                  <th>Vertrieb</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr
                    key={a.accountId}
                    onClick={() => navigate(`/admin/kunden/${a.accountId}`)}
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && navigate(`/admin/kunden/${a.accountId}`)
                    }
                  >
                    <td>
                      <strong>{a.name}</strong>
                    </td>
                    <td>
                      <code>{a.accountNumber}</code>
                    </td>
                    <td>{KONTOTYP[a.accountType] ?? a.accountType}</td>
                    <td>
                      <StatusPill active={a.isActive} />
                    </td>
                    <td className="num">
                      {a.currentBaseDiscountPercent === null ? (
                        <span className="pill pill-warn">
                          Kondition noch nicht eingerichtet
                        </span>
                      ) : (
                        pct(a.currentBaseDiscountPercent)
                      )}
                    </td>
                    <td className="num">{a.activeDeviationCount}</td>
                    <td>{datum(a.currentValidFrom)}</td>
                    <td className="num">{a.userCount}</td>
                    <td>
                      {a.salesOwner?.displayName ?? <span className="muted">–</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
